import React, { useState } from 'react';
import Button, { IconButton } from '../components/Button';
import {
  SkeletonLoader,
  MemberSkeleton,
  StatCardSkeleton,
  TableSkeleton,
  DashboardWidgetSkeleton
} from '../components/SkeletonLoader';
import {
  PlusIcon,
  CheckIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  SparklesIcon,
  HeartIcon,
  StarIcon,
} from '@heroicons/react/24/outline';

const ComponentsDemo: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showSkeletons, setShowSkeletons] = useState(true);

  const handleLoadingDemo = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 2000);
  };

  const sectionClass = 'bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700';

  return (
    <div className="min-h-screen font-sans text-gray-900 dark:text-slate-200" style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}>
      <div className="max-w-7xl mx-auto py-2 sm:py-4">
        {/* Hero Section */}
        <section className="pt-2 pb-6 px-4 sm:px-6">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600/80 dark:text-amber-400/80 font-semibold mb-3">
              ADMIN · Design System
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight" style={{ letterSpacing: '-0.04em' }}>
              Bauteil-<br /><span className="text-amber-600 dark:text-amber-400">Lager.</span>
            </h1>
            <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
              Alle Buttons, Badges, Skeletons & Farb-Tokens auf einen Blick — die Werkbank für Frontend-Konsistenz.
              Diese Demo-Seite zeigt das Standard-System; <span className="font-bold text-amber-700 dark:text-amber-300">Story-Hero v2</span> wird in den Pages eingesetzt.
            </p>
          </div>
        </section>

        <div className="px-4 sm:px-6 space-y-6">
          {/* Button Hierarchie */}
          <section className={`rounded-3xl p-6 space-y-4 ${sectionClass}`}>
            <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2">
              <span className="bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-300 px-2 py-1 rounded text-[11px] font-bold uppercase tracking-widest">NEU</span>
              Button-Hierarchie
            </h2>
            <p className="text-gray-600 dark:text-slate-400 text-sm">
              Klare Unterscheidung zwischen Haupt-, Neben- und optionalen Aktionen
            </p>

            <div className="space-y-6">
              {/* Primary Buttons */}
              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-3 uppercase tracking-widest">
                  Primary - Hauptaktionen
                </h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary">Primary Button</Button>
                  <Button variant="primary" size="sm">Small</Button>
                  <Button variant="primary" size="lg">Large</Button>
                  <Button variant="primary" loading={loading} onClick={handleLoadingDemo}>
                    {loading ? 'Wird geladen...' : 'Mit Loading'}
                  </Button>
                  <Button variant="primary" icon={<PlusIcon className="h-5 w-5" />}>
                    Mit Icon
                  </Button>
                  <Button variant="primary" icon={<CheckIcon className="h-5 w-5" />} iconPosition="right">
                    Icon Rechts
                  </Button>
                </div>
              </div>

              {/* Secondary Buttons */}
              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-3 uppercase tracking-widest">
                  Secondary - Nebenaktionen
                </h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="secondary">Secondary Button</Button>
                  <Button variant="secondary" size="sm">Small</Button>
                  <Button variant="secondary" size="lg">Large</Button>
                  <Button variant="secondary" icon={<InformationCircleIcon className="h-5 w-5" />}>
                    Mit Icon
                  </Button>
                </div>
              </div>

              {/* Tertiary Buttons */}
              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-3 uppercase tracking-widest">
                  Tertiary - Optionale Aktionen
                </h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="tertiary">Tertiary Button</Button>
                  <Button variant="tertiary" size="sm">Small</Button>
                  <Button variant="tertiary" size="lg">Large</Button>
                  <Button variant="tertiary" icon={<StarIcon className="h-5 w-5" />}>
                    Mit Icon
                  </Button>
                </div>
              </div>

              {/* Feedback Buttons */}
              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-3 uppercase tracking-widest">
                  Feedback - Success / Warning / Error
                </h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="success" icon={<CheckIcon className="h-5 w-5" />}>
                    Erfolg
                  </Button>
                  <Button variant="warning" icon={<ExclamationTriangleIcon className="h-5 w-5" />}>
                    Warnung
                  </Button>
                  <Button variant="error" icon={<TrashIcon className="h-5 w-5" />}>
                    Löschen
                  </Button>
                </div>
              </div>

              {/* Icon Buttons */}
              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-3 uppercase tracking-widest">
                  Icon Buttons
                </h3>
                <div className="flex flex-wrap gap-3">
                  <IconButton variant="primary" icon={<PlusIcon className="h-5 w-5" />} ariaLabel="Hinzufügen" />
                  <IconButton variant="secondary" icon={<CheckIcon className="h-5 w-5" />} ariaLabel="Bestätigen" />
                  <IconButton variant="tertiary" icon={<HeartIcon className="h-5 w-5" />} ariaLabel="Favorit" />
                  <IconButton variant="error" icon={<TrashIcon className="h-5 w-5" />} ariaLabel="Löschen" size="sm" />
                  <IconButton variant="success" icon={<CheckIcon className="h-5 w-5" />} ariaLabel="Erfolg" size="lg" />
                </div>
              </div>

              {/* Disabled State */}
              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-3 uppercase tracking-widest">
                  Disabled State
                </h3>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary" disabled>Deaktiviert</Button>
                  <Button variant="secondary" disabled>Deaktiviert</Button>
                  <Button variant="tertiary" disabled>Deaktiviert</Button>
                </div>
              </div>
            </div>
          </section>

          {/* Farbsystem */}
          <section className={`rounded-3xl p-6 space-y-4 ${sectionClass}`}>
            <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2">
              <span className="bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-300 px-2 py-1 rounded text-[11px] font-bold uppercase tracking-widest">NEU</span>
              Einheitliches Farbsystem
            </h2>
            <p className="text-gray-600 dark:text-slate-400 text-sm">
              Konsistente Farben für Feedback und Status-Anzeigen
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Success */}
              <div className="space-y-2">
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 uppercase tracking-widest">Success</h3>
                <div className="space-y-1">
                  <div className="h-10 bg-success-600 rounded flex items-center justify-center text-white text-sm font-medium">success-600</div>
                  <div className="h-8 bg-success-500 rounded flex items-center justify-center text-white text-xs">success-500</div>
                  <div className="h-8 bg-success-700 rounded flex items-center justify-center text-white text-xs">success-700</div>
                </div>
                <div className="p-3 bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 rounded-lg text-sm">
                  Erfolgreich gespeichert!
                </div>
              </div>

              {/* Warning */}
              <div className="space-y-2">
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 uppercase tracking-widest">Warning</h3>
                <div className="space-y-1">
                  <div className="h-10 bg-warning-600 rounded flex items-center justify-center text-white text-sm font-medium">warning-600</div>
                  <div className="h-8 bg-warning-500 rounded flex items-center justify-center text-white text-xs">warning-500</div>
                  <div className="h-8 bg-warning-700 rounded flex items-center justify-center text-white text-xs">warning-700</div>
                </div>
                <div className="p-3 bg-amber-500/15 border border-amber-500/30 text-amber-200 rounded-lg text-sm">
                  Bitte überprüfen Sie Ihre Eingabe
                </div>
              </div>

              {/* Error */}
              <div className="space-y-2">
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 uppercase tracking-widest">Error</h3>
                <div className="space-y-1">
                  <div className="h-10 bg-error-600 rounded flex items-center justify-center text-white text-sm font-medium">error-600</div>
                  <div className="h-8 bg-error-500 rounded flex items-center justify-center text-white text-xs">error-500</div>
                  <div className="h-8 bg-error-700 rounded flex items-center justify-center text-white text-xs">error-700</div>
                </div>
                <div className="p-3 bg-rose-500/15 border border-rose-500/30 text-rose-200 rounded-lg text-sm">
                  Ein Fehler ist aufgetreten
                </div>
              </div>

              {/* Info */}
              <div className="space-y-2">
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 uppercase tracking-widest">Info</h3>
                <div className="space-y-1">
                  <div className="h-10 bg-info-600 rounded flex items-center justify-center text-white text-sm font-medium">info-600</div>
                  <div className="h-8 bg-info-500 rounded flex items-center justify-center text-white text-xs">info-500</div>
                  <div className="h-8 bg-info-700 rounded flex items-center justify-center text-white text-xs">info-700</div>
                </div>
                <div className="p-3 bg-blue-500/15 border border-blue-500/30 text-blue-200 rounded-lg text-sm">
                  Wichtige Information
                </div>
              </div>
            </div>
          </section>

          {/* Skeleton Loader */}
          <section className={`rounded-3xl p-6 space-y-4 ${sectionClass}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2">
                  <span className="bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-300 px-2 py-1 rounded text-[11px] font-bold uppercase tracking-widest">NEU</span>
                  Skeleton Loader
                </h2>
                <p className="text-gray-600 dark:text-slate-400 text-sm">Statt "Lade..." Text für bessere User Experience</p>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setShowSkeletons(!showSkeletons)}>
                {showSkeletons ? 'Geladene Ansicht' : 'Skeleton Ansicht'}
              </Button>
            </div>

            <div className="space-y-6">
              {/* Basic Skeleton */}
              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-3 uppercase tracking-widest">Basic Skeleton Loader</h3>
                <div className="space-y-2">
                  <SkeletonLoader variant="text" width="100%" height={20} />
                  <SkeletonLoader variant="text" width="80%" height={20} />
                  <SkeletonLoader variant="text" width="60%" height={20} />
                </div>
              </div>

              {/* Stat Cards */}
              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-3 uppercase tracking-widest">Stat Cards Skeleton</h3>
                {showSkeletons ? (
                  <StatCardSkeleton count={4} />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: 'Mitglieder', value: '38', sub: '+2 diesen Monat' },
                      { label: 'Gäste', value: '20', sub: '5 aktiv' },
                      { label: 'Aktive Hunde', value: '89', sub: '99% komplett' },
                      { label: 'Verzehrkosten', value: '€57', sub: 'Unbezahlt' },
                    ].map((s) => (
                      <div key={s.label} className="rounded-2xl p-4 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">{s.label}</div>
                        <div className="text-3xl font-black text-gray-900 dark:text-white mt-1 tabular-nums">{s.value}</div>
                        <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">{s.sub}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Member List */}
              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-3 uppercase tracking-widest">Member List Skeleton</h3>
                {showSkeletons ? (
                  <MemberSkeleton count={3} />
                ) : (
                  <div className="space-y-3">
                    {[
                      { initials: 'MT', name: 'Markus Tietke', role: 'Vorstand, Mitglied', tone: 'amber' },
                      { initials: 'LB', name: 'Lara Bergmann', role: 'Mitglied', tone: 'blue' },
                    ].map((m) => (
                      <div key={m.initials} className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700">
                        <div className={`h-12 w-12 rounded-xl flex items-center justify-center font-bold ${
                          m.tone === 'amber' ? 'bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-300' : 'bg-blue-500/15 border border-blue-500/30 text-blue-700 dark:text-blue-300'
                        }`}>
                          {m.initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-gray-900 dark:text-white">{m.name}</div>
                          <div className="text-sm text-gray-600 dark:text-slate-400">{m.role}</div>
                        </div>
                        <Button variant="tertiary" size="sm">Details</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Table Skeleton */}
              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-3 uppercase tracking-widest">Table Skeleton</h3>
                {showSkeletons ? (
                  <TableSkeleton rows={4} />
                ) : (
                  <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-slate-700">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-slate-900/60">
                        <tr>
                          <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 dark:text-slate-500 uppercase tracking-widest">Name</th>
                          <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 dark:text-slate-500 uppercase tracking-widest">Status</th>
                          <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 dark:text-slate-500 uppercase tracking-widest">Rolle</th>
                          <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 dark:text-slate-500 uppercase tracking-widest">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-slate-800">
                        <tr>
                          <td className="px-4 py-3 text-gray-900 dark:text-white">Markus Tietke</td>
                          <td className="px-4 py-3"><span className="px-2 py-1 bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-xs rounded">Aktiv</span></td>
                          <td className="px-4 py-3 text-gray-600 dark:text-slate-400">Vorstand</td>
                          <td className="px-4 py-3"><Button variant="tertiary" size="sm">Bearbeiten</Button></td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-gray-900 dark:text-white">Lara Bergmann</td>
                          <td className="px-4 py-3"><span className="px-2 py-1 bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-xs rounded">Aktiv</span></td>
                          <td className="px-4 py-3 text-gray-600 dark:text-slate-400">Mitglied</td>
                          <td className="px-4 py-3"><Button variant="tertiary" size="sm">Bearbeiten</Button></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Widget Skeleton */}
              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-3 uppercase tracking-widest">Dashboard Widget Skeleton</h3>
                {showSkeletons ? (
                  <DashboardWidgetSkeleton />
                ) : (
                  <div className="rounded-2xl p-6 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700">
                    <div className="flex items-center justify-between mb-4">
                      <div className="h-10 w-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                        <SparklesIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div className="text-2xl font-black text-gray-900 dark:text-white tabular-nums">12</div>
                    </div>
                    <div className="text-gray-900 dark:text-white font-bold">Aktive Trainings</div>
                    <div className="text-sm text-gray-600 dark:text-slate-400">3 heute geplant</div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Usage Examples */}
          <section className={`rounded-3xl p-6 space-y-4 ${sectionClass}`}>
            <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight">Code-Beispiele</h2>

            <div className="space-y-4">
              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-2 uppercase tracking-widest">Button Verwendung</h3>
                <pre className="p-4 rounded-2xl overflow-x-auto text-sm bg-gray-100 dark:bg-slate-950/55 border border-gray-200 dark:border-slate-800">
                  <code className="text-gray-700 dark:text-slate-300">{`// Primary Button
<Button variant="primary">Speichern</Button>

// Mit Icon
<Button variant="primary" icon={<PlusIcon />}>
  Hinzufügen
</Button>

// Mit Loading State
<Button variant="primary" loading={isLoading}>
  Speichern
</Button>

// Secondary / Tertiary
<Button variant="secondary">Abbrechen</Button>
<Button variant="tertiary">Optionen</Button>

// Feedback Buttons
<Button variant="success">Bestätigen</Button>
<Button variant="warning">Warnung</Button>
<Button variant="error">Löschen</Button>`}</code>
                </pre>
              </div>

              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-2 uppercase tracking-widest">Skeleton Loader Verwendung</h3>
                <pre className="p-4 rounded-2xl overflow-x-auto text-sm bg-gray-100 dark:bg-slate-950/55 border border-gray-200 dark:border-slate-800">
                  <code className="text-gray-700 dark:text-slate-300">{`// Während Daten laden
{loading ? (
  <MemberSkeleton count={5} />
) : (
  <MemberList data={members} />
)}

// Stat Cards
{loading ? (
  <StatCardSkeleton count={4} />
) : (
  <StatsGrid stats={stats} />
)}

// Custom Skeleton
<SkeletonLoader
  variant="text"
  width="60%"
  height={20}
/>`}</code>
                </pre>
              </div>

              <div>
                <h3 className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 mb-2 uppercase tracking-widest">Farben Verwendung</h3>
                <pre className="p-4 rounded-2xl overflow-x-auto text-sm bg-gray-100 dark:bg-slate-950/55 border border-gray-200 dark:border-slate-800">
                  <code className="text-gray-700 dark:text-slate-300">{`// Success
<div className="bg-success-600 text-white">
  Erfolgreich!
</div>

// Warning
<div className="bg-warning-600 text-white">
  Achtung!
</div>

// Error
<div className="bg-error-600 text-white">
  Fehler!
</div>

// Info
<div className="bg-info-600 text-white">
  Information
</div>`}</code>
                </pre>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ComponentsDemo;
